import { useQuery } from 'react-query'
import { Datasource, DatasourceListResponse, DatasourceFilters } from './types'
import { mockDatasources } from '@/mocks/datasources/mockData'

export const useDatasource = (datasourceId: string) => {
  return useQuery<Datasource, Error>(
    ['datasource', datasourceId],
    async () => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      const datasource = mockDatasources.find((d) => d.id === datasourceId)
      if (!datasource) {
        throw new Error('Datasource not found')
      }

      return datasource
    },
    {
      enabled: !!datasourceId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  )
}

interface UseDatasourcesParams {
  page?: number
  limit?: number
  filters?: DatasourceFilters
}

export const useDatasources = ({
  page = 1,
  limit = 10,
  filters = {},
}: UseDatasourcesParams = {}) => {
  return useQuery<DatasourceListResponse, Error>(
    ['datasources', page, limit, filters],
    async () => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      let filteredDatasources = [...mockDatasources]

      // Apply filters
      if (filters.type) {
        filteredDatasources = filteredDatasources.filter(
          (d) => d.type === filters.type,
        )
      }

      if (filters.status) {
        filteredDatasources = filteredDatasources.filter(
          (d) => d.connectionStatus === filters.status,
        )
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        filteredDatasources = filteredDatasources.filter(
          (d) =>
            d.name.toLowerCase().includes(searchLower) ||
            d.id.toLowerCase().includes(searchLower),
        )
      }

      // Pagination
      const start = (page - 1) * limit
      const end = start + limit
      const paginatedDatasources = filteredDatasources.slice(start, end)

      return {
        datasources: paginatedDatasources,
        total: filteredDatasources.length,
        page,
        limit,
        hasMore: end < filteredDatasources.length,
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  )
}
